import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts'
import { CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard } from './shared'
import { downloadOpportunityQueueCsv } from '@/services/reports-service'
import { useState } from 'react'

interface Row {
  estimatorId: string | null
  estimatorName: string
  opportunities: Array<{
    id: string
    title: string
    lead: string | null
    estimatorNotes: string | null
    createdAt: string
    dueDate: string | null
    projectCity: string | null
    projectAddress: string | null
    projectTypes: string
    contactName: string | null
    contactEmail: string | null
    contactPhone: string | null
    daysUntilDue: number | null
    aging: number
  }>
}

interface ReportData {
  summary: {
    totalOpen: number
    dueThisWeek: number
    overdue: number
    unassignedCount: number
  }
  chartData: {
    workloadByEstimator: Array<{ estimatorId: string | null; estimatorName: string; count: number }>
  }
  rows: Row[]
}

function formatUsDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`
}

function CountdownChip({ daysUntilDue, dueDate }: { daysUntilDue: number | null; dueDate: string | null }) {
  if (dueDate == null) return <span className="rep-countdown-tbd">tbd</span>
  const dateStr = formatUsDate(dueDate)
  if (daysUntilDue == null || daysUntilDue >= 8) return <span>{dateStr}</span>
  if (daysUntilDue > 0) {
    return <span>{dateStr} <span className="rep-countdown-chip rep-countdown-chip-urgent">{daysUntilDue}d left</span></span>
  }
  if (daysUntilDue === 0) {
    return <span>{dateStr} <span className="rep-countdown-chip rep-countdown-chip-overdue">Due today</span></span>
  }
  return <span>{dateStr} <span className="rep-countdown-chip rep-countdown-chip-overdue">{Math.abs(daysUntilDue)}d overdue</span></span>
}

export function OpportunityQueueReport({ data, filters }: { data: ReportData; filters: any }) {
  const { summary, chartData, rows } = data
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  async function handleDownload() {
    setDownloading(true)
    setDownloadError(null)
    try {
      const { blob, filename } = await downloadOpportunityQueueCsv(filters)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download opportunity-queue CSV:', err)
      setDownloadError('Could not download CSV. Try again.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Total Open" value={summary.totalOpen.toLocaleString()} color="#0074F4" />
        <StatCard title="Due This Week" value={summary.dueThisWeek.toLocaleString()} color="#8B47FB" />
        <StatCard title="Overdue" value={summary.overdue.toLocaleString()} color="#E5484D" />
        <StatCard title="Unassigned" value={summary.unassignedCount.toLocaleString()} color="#7CDBE7" />
      </div>

      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <h3 className="rep-chart-title">Open opportunities per estimator</h3>
          {chartData.workloadByEstimator.length === 0 ? (
            <p className="rep-empty">No opportunities to chart.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(120, chartData.workloadByEstimator.length * 36)}>
              <BarChart layout="vertical" data={chartData.workloadByEstimator}
                        margin={{ top: 8, right: 32, bottom: 8, left: 24 }}>
                <XAxis type="number" allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="estimatorName"
                  width={140}
                  interval={0}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {chartData.workloadByEstimator.map((entry, i) => (
                    <Cell key={i} fill={entry.estimatorId == null ? 'var(--s500)' : CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                  <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: 'var(--s700)' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rep-queue-section">
        <div className="rep-queue-section-header">
          <h3 className="rep-chart-title">Queue</h3>
          <button
            type="button"
            className="rep-queue-download-btn"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? 'Downloading…' : 'Download CSV'}
          </button>
        </div>

        {downloadError && (
          <div className="rep-export-error" role="alert">
            {downloadError}
            <button type="button" className="rep-export-error-dismiss" onClick={() => setDownloadError(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="rep-empty">No outstanding opportunities.</p>
        ) : (
          rows.map((group) => (
            <div className="rep-queue-group" key={group.estimatorId ?? 'unassigned'}>
              <div className="rep-queue-group-header">
                <span className="rep-queue-group-name">{group.estimatorName}</span>
                <span className="rep-queue-count-badge">{group.opportunities.length}</span>
              </div>
              <div className="rep-queue-table-card">
                <table className="rep-queue-table">
                  <colgroup>
                    <col className="rep-queue-col-date" />
                    <col className="rep-queue-col-bid" />
                    <col className="rep-queue-col-customer" />
                    <col className="rep-queue-col-location" />
                    <col className="rep-queue-col-contact" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Date Added</th>
                      <th>Bid Due</th>
                      <th>Customer</th>
                      <th>Location</th>
                      <th>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.opportunities.map((opp) => (
                      <tr key={opp.id}>
                        <td>{formatUsDate(opp.createdAt)}</td>
                        <td><CountdownChip daysUntilDue={opp.daysUntilDue} dueDate={opp.dueDate} /></td>
                        <td>{opp.title.replace(/^\[SEED\]\s*/i, '')}</td>
                        <td>{opp.projectCity ?? opp.projectAddress ?? '—'}</td>
                        <td>
                          <div className="rep-queue-contact">
                            {opp.contactName && <div className="rep-queue-contact-name">{opp.contactName}</div>}
                            {opp.contactPhone && <div className="rep-queue-contact-info">{opp.contactPhone}</div>}
                            {opp.contactEmail && <div className="rep-queue-contact-info">{opp.contactEmail}</div>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
